import React from 'react';
import { Placeholder, VisitorIdentification } from '@sitecore-jss/sitecore-jss-react';
import styled, { css, keyframes } from 'styled-components';
import { WithContext, Organization } from 'schema-dts';
import { JsonLd } from 'react-schemaorg';

// import { withTranslation } from 'react-i18next';
import MenuProvider from '_containers/MenuContext';
import ModalProvider from '_containers/ModalContext';
import Modal from '_components/Corporate/Modal';
import { buildSearchUrl } from '_utils/helpers';
import { TRANSITION_DELAY } from '_utils/components/Corporate/Scroller/';
import { spacing } from '_utils/props';
import { cssInlineSvg } from '_utils/styles/svg';
import { depth } from '_utils/styles';

interface LayoutProps {
  context: any;
  route: any;
  loading: boolean;
}

/*
  DEFAULT APP LAYOUT
*/

const StructuredData: React.FC<LayoutProps> = ({ context }) => {
  if (typeof context.siteInfo === 'undefined') {
    return null;
  }

  const {
    siteInfo: { siteBaseUrl: url }
  } = context;

  const OrganizationData: WithContext<Organization> = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    url: url,
    logo: `${url}/dist/cityofsydney/cos-logo.jpg`
  };

  // This should be of type WebSite not any
  // But WebSite type doesn't like 'query-input' which is required for SearchAction
  // @see https://developers.google.com/search/docs/data-types/sitelinks-searchbox#potential-action-query-input
  const WebSiteData: WithContext<any> = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: url,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${url}${buildSearchUrl({ term: '' })}{search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };

  return (
    <>
      <JsonLd item={OrganizationData} />
      <JsonLd item={WebSiteData} />
    </>
  );
};

export const MainContainer = styled.div`
  flex: 1 0 auto;

  > * {
    &:last-child {
      ${({ theme }) => spacing({ theme, $pb: 7 })}
    }
  }
`;

const pulse = keyframes`
  100% {
    opacity: 0;
  }
`;

const LoadingScreen = styled.div<{ $active: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: white;
  z-index: ${depth.LOADING_SCREEN};

  ${({ $active }) => css`
      opacity: ${$active ? 1 : 0};
      pointer-events: ${$active ? 'auto' : 'none'};
      transition: ${$active ? TRANSITION_DELAY - 50 : 400}ms opacity ease-in;
    `}

  > div {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 6vw;
    height: 6vh;
    display: block;
    animation: 2000ms ${pulse} linear infinite alternate;

    &:before {
      content: '';
      width: 100%;
      height: 100%;
      display: block;
      background-image: ${cssInlineSvg(
        `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21.1642 11.8118C21.1642 11.3882 20.8426 11.0588 20.4292 11.0588C20.0157 11.0588 19.6941 11.3882 19.6941 11.8118C19.6941 12.2353 20.0157 12.5647 20.4292 12.5647C20.8426 12.5647 21.1642 12.1882 21.1642 11.8118Z" fill="black"/><path d="M20.4751 0C9.67898 0 0.950195 8.94118 0.950195 20C0.950195 31.0588 9.67898 40 20.4751 40C31.2712 40 40 31.0588 40 20C40 8.94118 31.2712 0 20.4751 0ZM20.4751 24.7059C18.8672 24.7059 17.1214 24.9882 15.5135 25.3647L15.2838 23.7176C16.9376 23.2941 18.7293 23.0588 20.4292 23.0588C22.129 23.0588 23.9666 23.2941 25.6205 23.7647L25.3908 25.4118C23.8747 24.9412 22.129 24.7059 20.4751 24.7059ZM25.3908 26.0706L25.207 27.6706C23.691 27.2471 22.0371 26.9647 20.521 26.9647C19.005 26.9647 17.3511 27.2 15.881 27.6235L15.6972 26.0235C17.2592 25.6 18.9591 25.3647 20.567 25.3647C22.083 25.3647 23.8288 25.6471 25.3908 26.0706ZM19.1888 20.7529L20.4292 20.7059L21.6696 20.7529V22.4L20.4292 22.3529L19.1888 22.4V20.7529ZM22.7721 22.4941L22.9559 20.8471C23.9207 20.9412 25.0692 21.1765 25.988 21.4118L25.7583 23.0588C25.0233 22.8706 23.7828 22.6353 22.7721 22.4941ZM17.9483 4.32941C17.9483 4.32941 19.6941 4.70588 19.8779 4.75294C19.9238 4.56471 20.521 2.82353 20.521 2.82353C20.521 2.82353 21.0723 4.56471 21.1642 4.75294C21.348 4.70588 23.0937 4.32941 23.0937 4.32941C23.0937 4.32941 21.8993 5.69412 21.8074 5.83529C21.8993 5.97647 23.0937 7.34118 23.0937 7.34118C23.0937 7.34118 21.348 6.96471 21.1642 6.91765C21.1183 7.10588 20.521 8.84706 20.521 8.84706C20.521 8.84706 19.9698 7.10588 19.8779 6.91765C19.6941 6.96471 17.9483 7.34118 17.9483 7.34118C17.9483 7.34118 19.1428 5.97647 19.2806 5.83529C19.1428 5.74118 17.9483 4.32941 17.9483 4.32941ZM15.5135 16.4235L19.6482 15.8118L19.8779 13.8353C18.7753 13.5529 18.0402 12.3294 18.4537 11.0588C18.6834 10.4471 19.1887 9.97647 19.786 9.78823C21.1642 9.41176 22.4046 10.4471 22.4046 11.8118C22.4046 12.8 21.7155 13.6471 20.7967 13.8353L21.0264 15.8118L25.1611 16.4235V17.0824L21.2561 17.6471L21.5317 19.9059H19.0969L19.3725 17.6471L15.4675 17.0824V16.4235H15.5135ZM17.9483 20.8941L18.0862 22.5412C17.1673 22.6353 15.9269 22.8706 15.1919 23.0588L14.9622 21.4118C15.881 21.1765 17.0295 20.9882 17.9483 20.8941ZM9.86274 20.4706L11.5166 25.6L9.90868 25.5529L8.30075 25.6471L9.86274 20.4706ZM20.4751 35.8118C15.5594 35.8118 9.86274 31.7176 9.77086 26.4H10.1843C11.2869 30.4941 15.881 33.3647 19.9238 33.6C19.6941 32.4235 19.4185 30.4 19.2806 27.9059L20.521 27.8588L21.7614 27.9059C21.5777 30.4 21.2561 32.8 21.1183 33.6C25.1611 33.3647 29.7552 30.4941 30.8577 26.4H31.2712C31.0875 31.7176 25.3908 35.8118 20.4751 35.8118ZM31.0415 25.5059L29.4336 25.5529L31.0874 20.4235L32.6954 25.6L31.0415 25.5059Z" fill="black"/></svg>`
      )};
      background-size: contain;
      background-position: center center;
      background-repeat: no-repeat;

      ${({ $active }) => css`
          opacity: ${$active ? 1 : 0};
          transition: 400ms opacity ease-in ${$active ? 1500 : 0}ms;
        `}
    }
  }
`;

const DefaultLayout: React.FC<LayoutProps> = (props: LayoutProps) => {
  const { route, loading } = props;

  return (
    <>
      <MenuProvider>
        <ModalProvider>
          <Placeholder name="jss-header" rendering={route} />
          <MainContainer role="main" id="main-content">
            <Placeholder name="jss-main" rendering={route} />
          </MainContainer>
          <Placeholder name="jss-footer" rendering={route} />
          <Modal />
          <StructuredData {...props} />
        </ModalProvider>
      </MenuProvider>
      <LoadingScreen $active={loading}>
        <div />
      </LoadingScreen>
      <VisitorIdentification />
    </>
  );
};

export default DefaultLayout;
